import { useState, useMemo, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { LocationAutocomplete } from '../LocationAutocomplete';
import {
  CREATE_APPOINTMENT,
  UPDATE_APPOINTMENT,
  DELETE_APPOINTMENT,
  LIST_MY_APPOINTMENTS,
} from '../../graphql/appointments';
import { gql } from '@apollo/client';

const LIST_CLIENTS = gql`
  query ListClients {
    listClients {
      id
      firstName
      lastName
      email
      role
    }
  }
`;

const LIST_CASES = gql`
  query ListCases {
    listCases {
      items {
        id
        caseNumber
        clientId
        status
        caseType
        client {
          id
          firstName
          lastName
          email
        }
      }
    }
  }
`;

const LIST_STAFF = gql`
  query ListStaff {
    listStaff {
      id
      firstName
      lastName
      email
      role
    }
  }
`;

interface Client {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface Case {
  id: string;
  caseNumber: string;
  clientId: string;
  status: string;
  caseType?: string;
  client?: Client;
}

interface StaffMember {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
}

interface ListClientsData {
  listClients: Client[];
}

interface ListCasesData {
  listCases: {
    items: Case[];
  };
}

interface ListStaffData {
  listStaff: StaffMember[];
}

interface AppointmentModalProps {
  appointment?: any;
  onClose: () => void;
  preSelectedCaseId?: string;
  preSelectedClientId?: string;
  preFilledDateTime?: { date: Date; hour: number } | null;
}

type SelectionStep = 'client' | 'case' | 'form';

export function AppointmentModal({ appointment, onClose, preSelectedCaseId, preSelectedClientId, preFilledDateTime }: AppointmentModalProps) {
  const isEditing = !!appointment;

  // Helper to convert Date to datetime-local format in local timezone
  const toLocalDateTimeString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const getDefaultStartTime = () => {
    if (appointment?.startTime) {
      return toLocalDateTimeString(new Date(appointment.startTime));
    }
    if (preFilledDateTime) {
      const date = new Date(preFilledDateTime.date);
      date.setHours(preFilledDateTime.hour, 0, 0, 0);
      return toLocalDateTimeString(date);
    }
    return '';
  };

  const getDefaultEndTime = () => {
    if (appointment?.endTime) {
      return toLocalDateTimeString(new Date(appointment.endTime));
    }
    if (preFilledDateTime) {
      const date = new Date(preFilledDateTime.date);
      date.setHours(preFilledDateTime.hour + 1, 0, 0, 0);
      return toLocalDateTimeString(date);
    }
    return '';
  };

  // Determine initial step
  const getInitialStep = (): SelectionStep => {
    if (isEditing || preSelectedCaseId) return 'form';
    if (preSelectedClientId) return 'case';
    return 'client';
  };

  const [step, setStep] = useState<SelectionStep>(getInitialStep);
  const [clientSearch, setClientSearch] = useState('');
  const [caseSearch, setCaseSearch] = useState('');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  const [formData, setFormData] = useState({
    clientId: appointment?.clientId || preSelectedClientId || '',
    caseId: appointment?.caseId || preSelectedCaseId || '',
    title: appointment?.title || '',
    description: appointment?.description || '',
    startTime: getDefaultStartTime(),
    endTime: getDefaultEndTime(),
    locationName: appointment?.locationName || '',
    location: appointment?.location || '',
    attendeeIds: appointment?.attendeeIds || [],
    guestEmails: appointment?.guestEmails || [] as string[],
    guests: appointment?.guests || [] as Array<{ name: string; email: string }>,
  });

  const [guestNameInput, setGuestNameInput] = useState('');
  const [guestEmailInput, setGuestEmailInput] = useState('');

  const { data: clientsData, loading: clientsLoading } = useQuery<ListClientsData>(LIST_CLIENTS);
  const { data: casesData, loading: casesLoading } = useQuery<ListCasesData>(LIST_CASES);
  const { data: staffData } = useQuery<ListStaffData>(LIST_STAFF);

  const [createAppointment, { loading: creating }] = useMutation(CREATE_APPOINTMENT, {
    refetchQueries: [{ query: LIST_MY_APPOINTMENTS }],
  });

  const [updateAppointment, { loading: updating }] = useMutation(UPDATE_APPOINTMENT, {
    refetchQueries: [{ query: LIST_MY_APPOINTMENTS }],
  });

  const [deleteAppointment, { loading: deleting }] = useMutation(DELETE_APPOINTMENT, {
    refetchQueries: [{ query: LIST_MY_APPOINTMENTS }],
  });

  const clients: Client[] = clientsData?.listClients || [];
  const allCases: Case[] = casesData?.listCases?.items || [];
  const staff = staffData?.listStaff || [];

  // Set selected client from preSelectedClientId or appointment
  useEffect(() => {
    const clientId = formData.clientId || preSelectedClientId;
    if (clientId && clients.length > 0) {
      const client = clients.find((c) => c.id === clientId);
      if (client) setSelectedClient(client);
    }
  }, [formData.clientId, preSelectedClientId, clients]);

  // Filter clients by search
  const filteredClients = useMemo(() => {
    if (!clientSearch.trim()) return clients;
    const query = clientSearch.toLowerCase();
    return clients.filter((client) => {
      const fullName = `${client.firstName} ${client.lastName}`.toLowerCase();
      const email = client.email?.toLowerCase() || '';
      return fullName.includes(query) || email.includes(query);
    });
  }, [clients, clientSearch]);

  // Filter cases by selected client
  const clientCases = useMemo(() => {
    const clientId = selectedClient?.id || formData.clientId;
    if (!clientId) return [];
    return allCases.filter((c) => c.clientId === clientId);
  }, [allCases, selectedClient, formData.clientId]);

  // Filter cases by search
  const filteredCases = useMemo(() => {
    if (!caseSearch.trim()) return clientCases;
    const query = caseSearch.toLowerCase();
    return clientCases.filter((c) => {
      return (
        c.caseNumber.toLowerCase().includes(query) ||
        c.status.toLowerCase().includes(query) ||
        (c.caseType?.toLowerCase() || '').includes(query)
      );
    });
  }, [clientCases, caseSearch]);

  // Get selected case info for display
  const selectedCase = useMemo(() => {
    if (!formData.caseId) return null;
    return allCases.find((c) => c.id === formData.caseId);
  }, [formData.caseId, allCases]);

  const handleSelectClient = (client: Client) => {
    setSelectedClient(client);
    setFormData((prev) => ({ ...prev, clientId: client.id, caseId: '' }));
    setClientSearch('');
    setStep('case');
  };

  const handleSelectCase = (caseItem: Case) => {
    setFormData((prev) => ({ ...prev, caseId: caseItem.id }));
    setCaseSearch('');
    setStep('form');
  };

  const handleBackToClients = () => {
    setSelectedClient(null);
    setFormData((prev) => ({ ...prev, clientId: '', caseId: '' }));
    setStep('client');
  };

  const handleChangeClientCase = () => {
    setStep('client');
    setClientSearch('');
    setCaseSearch('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.caseId) {
      alert('Please select a client and case for this appointment');
      return;
    }

    try {
      const input = {
        clientId: formData.clientId || null,
        caseId: formData.caseId,
        title: formData.title,
        description: formData.description,
        startTime: new Date(formData.startTime).toISOString(),
        endTime: new Date(formData.endTime).toISOString(),
        locationName: formData.locationName,
        location: formData.location,
        attendeeIds: formData.attendeeIds,
        guestEmails: formData.guestEmails,
        guests: formData.guests,
      };

      if (isEditing) {
        await updateAppointment({
          variables: { id: appointment.id, input },
        });
      } else {
        await createAppointment({
          variables: { input },
        });
      }

      onClose();
    } catch (error) {
      console.error('Error saving appointment:', error);
      alert('Failed to save appointment. Please try again.');
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this appointment?')) {
      return;
    }

    try {
      await deleteAppointment({
        variables: { id: appointment.id },
      });
      onClose();
    } catch (error) {
      console.error('Error deleting appointment:', error);
      alert('Failed to delete appointment. Please try again.');
    }
  };

  const handleAttendeeToggle = (staffId: string) => {
    setFormData((prev) => ({
      ...prev,
      attendeeIds: prev.attendeeIds.includes(staffId)
        ? prev.attendeeIds.filter((id: string) => id !== staffId)
        : [...prev.attendeeIds, staffId],
    }));
  };

  const handleAddGuest = () => {
    const name = guestNameInput.trim();
    const email = guestEmailInput.trim().toLowerCase();
    // Stricter email regex - only allows alphanumeric, dots, hyphens, underscores, and plus signs
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    
    if (!name) {
      alert('Please enter the guest name');
      return;
    }

    if (!emailRegex.test(email)) {
      alert('Please enter a valid email address');
      return;
    }

    if (formData.guests.some((g: { name: string; email: string }) => g.email === email)) {
      alert('This email has already been added');
      return;
    }

    setFormData((prev) => ({
      ...prev,
      guests: [...prev.guests, { name, email }],
      guestEmails: [...prev.guestEmails, email],
    }));
    setGuestNameInput('');
    setGuestEmailInput('');
  };

  const handleRemoveGuest = (email: string) => {
    setFormData((prev) => ({
      ...prev,
      guests: prev.guests.filter((g: { name: string; email: string }) => g.email !== email),
      guestEmails: prev.guestEmails.filter((e: string) => e !== email),
    }));
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE': return 'bg-green-100 text-green-800';
      case 'PENDING': return 'bg-yellow-100 text-yellow-800';
      case 'CLOSED': return 'bg-gray-100 text-gray-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  // Client Selection Step
  if (step === 'client') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] flex flex-col">
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-lg">
            <h2 className="text-xl font-bold text-gray-900">Select Client</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="p-4 border-b border-gray-100">
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search clients by name..."
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                autoFocus
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {clientsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-aalaw-red"></div>
              </div>
            ) : filteredClients.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {clientSearch ? 'No clients found' : 'No clients available'}
              </div>
            ) : (
              <div className="space-y-2">
                {filteredClients.map((client) => {
                  const caseCount = allCases.filter((c) => c.clientId === client.id).length;
                  const initials = `${client.firstName[0]}${client.lastName[0]}`.toUpperCase();
                  return (
                    <button
                      key={client.id}
                      onClick={() => handleSelectClient(client)}
                      className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors text-left border border-gray-200"
                    >
                      <div className="w-10 h-10 rounded-full bg-aalaw-red flex items-center justify-center text-white font-semibold">
                        {initials}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">
                          {client.firstName} {client.lastName}
                        </p>
                        <p className="text-sm text-gray-500">
                          {caseCount} {caseCount === 1 ? 'case' : 'cases'}
                        </p>
                      </div>
                      <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Case Selection Step
  if (step === 'case') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] flex flex-col">
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-3 rounded-t-lg">
            <button onClick={handleBackToClients} className="text-gray-400 hover:text-gray-600 transition-colors">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h2 className="text-xl font-bold text-gray-900 flex-1">Select Case</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Selected Client Banner */}
          {selectedClient && (
            <div className="px-6 py-3 bg-red-50 border-b border-red-100 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-aalaw-red flex items-center justify-center text-white text-sm font-semibold">
                {`${selectedClient.firstName[0]}${selectedClient.lastName[0]}`.toUpperCase()}
              </div>
              <div>
                <p className="font-medium text-gray-900">{selectedClient.firstName} {selectedClient.lastName}</p>
                <p className="text-sm text-gray-500">{clientCases.length} {clientCases.length === 1 ? 'case' : 'cases'} available</p>
              </div>
            </div>
          )}

          <div className="p-4 border-b border-gray-100">
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search cases..."
                value={caseSearch}
                onChange={(e) => setCaseSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                autoFocus
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {casesLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-aalaw-red"></div>
              </div>
            ) : filteredCases.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {caseSearch ? 'No cases found' : 'This client has no cases'}
              </div>
            ) : (
              <div className="space-y-2">
                {filteredCases.map((caseItem) => (
                  <button
                    key={caseItem.id}
                    onClick={() => handleSelectCase(caseItem)}
                    className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors text-left border border-gray-200"
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getStatusColor(caseItem.status)}`}>
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{caseItem.caseNumber}</p>
                      <p className="text-sm text-gray-500">
                        {caseItem.caseType ? `${caseItem.caseType} • ` : ''}{caseItem.status}
                      </p>
                    </div>
                    <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Main Form
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">
            {isEditing ? 'Edit Appointment' : 'New Appointment'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Client & Case Selection - Required */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Client & Case <span className="text-red-500">*</span>
            </label>
            {selectedClient && selectedCase ? (
              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-aalaw-red flex items-center justify-center text-white font-semibold">
                    {`${selectedClient.firstName[0]}${selectedClient.lastName[0]}`.toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {selectedClient.firstName} {selectedClient.lastName}
                    </p>
                    <p className="text-sm text-gray-500">Case: {selectedCase.caseNumber}</p>
                  </div>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={handleChangeClientCase}>
                  Change
                </Button>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleChangeClientCase}
                className="w-full flex items-center gap-3 p-4 border-2 border-dashed border-aalaw-red rounded-lg hover:bg-red-50 transition-colors"
              >
                <svg className="w-6 h-6 text-aalaw-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
                <span className="text-aalaw-red font-medium">Select client and case</span>
              </button>
            )}
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title <span className="text-red-500">*</span>
            </label>
            <Input
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Client Conference, Court Hearing"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
              placeholder="Add notes or details about this appointment"
            />
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Time <span className="text-red-500">*</span>
              </label>
              <input
                required
                type="datetime-local"
                value={formData.startTime}
                onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Time <span className="text-red-500">*</span>
              </label>
              <input
                required
                type="datetime-local"
                value={formData.endTime}
                onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
              />
            </div>
          </div>

          {/* Location */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Location Name</label>
              <Input
                value={formData.locationName}
                onChange={(e) => setFormData({ ...formData, locationName: e.target.value })}
                placeholder="e.g., Main Office, County Courthouse"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
              <LocationAutocomplete
                value={formData.location}
                onChange={(value) => setFormData({ ...formData, location: value })}
                placeholder="Search for address..."
              />
            </div>
          </div>

          {/* Attendees */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Staff Attendees (Optional)
            </label>
            <div className="border border-gray-300 rounded-lg p-4 max-h-48 overflow-y-auto">
              {staff.length === 0 ? (
                <p className="text-sm text-gray-500">Loading staff...</p>
              ) : (
                <div className="space-y-2">
                  {staff.map((s: any) => (
                    <label key={s.id} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.attendeeIds.includes(s.id)}
                        onChange={() => handleAttendeeToggle(s.id)}
                        className="rounded border-gray-300 text-aalaw-red focus:ring-aalaw-red"
                      />
                      <span className="text-sm text-gray-900">
                        {s.firstName} {s.lastName} ({s.role})
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* External Guests */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              External Guests (Optional)
            </label>
            <div className="flex gap-2 mb-2">
              <Input
                type="text"
                value={guestNameInput}
                onChange={(e) => setGuestNameInput(e.target.value)}
                placeholder="Guest Name"
                className="flex-1"
              />
              <Input
                type="email"
                value={guestEmailInput}
                onChange={(e) => setGuestEmailInput(e.target.value)}
                placeholder="guest@example.com"
                className="flex-1"
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddGuest();
                  }
                }}
              />
              <Button type="button" variant="outline" onClick={handleAddGuest}>
                Add
              </Button>
            </div>
            {formData.guests.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.guests.map((guest: { name: string; email: string }) => (
                  <span
                    key={guest.email}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-100 rounded-full text-sm"
                  >
                    <span className="font-medium">{guest.name}</span>
                    <span className="text-gray-500">({guest.email})</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveGuest(guest.email)}
                      className="text-gray-500 hover:text-red-500 ml-1"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <div>
              {isEditing && (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deleting}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </Button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Button 
                type="button" 
                variant="outline" 
                onClick={onClose}
                className="border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={creating || updating || !formData.caseId}
                className={!formData.caseId ? 'opacity-50 cursor-not-allowed' : ''}
              >
                {creating || updating ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Appointment'}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
